// src/pages/WebsiteDetail.js - opravené načítání a předávání dat
import React, {useEffect, useState} from 'react';
import { Container, Row, Col, Nav, Tab, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import useWebsiteStore from '../store/websiteStore';
import WebsitePerformance from "./WebsitePerformance";
import WebsiteOverview from "./WebsiteOverview";
import WebsiteBackups from "./WebsiteBackups";
import WebsiteSecurity from "./WebsiteSecurity";
import WebsiteSEO from "./WebsiteSEO";
import ErrorBoundary from '../components/ErrorBoundary';
import { WebsiteProvider, useWebsiteContext } from '../contexts/WebsiteContext';
import { websiteService } from "../api/services/websites";
import ApiKeyManager from "../components/Website/ApiKeyManager";

// Vnitřní komponenta, která využívá WebsiteContext
const WebsiteDetailContent = () => {
    const {
        website,
        websiteId,
        activeTab,
        setActiveTab,
        isLoading,
        navigateBack
    } = useWebsiteContext();

    const [monitoringData, setMonitoringData] = useState(null);
    const [dataLoading, setDataLoading] = useState(true);
    const [dataError, setDataError] = useState(null);
    const [specificData, setSpecificData] = useState({
        performance: null,
        seo: null,
        security: null
    });

    // Funkce pro načtení dat pro specifický tab
    const fetchSpecificTabData = async (websiteId, tabType) => {
        try {
            setDataLoading(true);
            const response = await websiteService.getMonitoringResults(websiteId, tabType);
            const data = response?.data;

            setSpecificData(prev => ({
                ...prev,
                [tabType]: data
            }));

            return data;
        } catch (error) {
            console.error(`Error fetching ${tabType} data:`, error);
            setDataError(`Nepodařilo se načíst data pro ${tabType}.`);
            return null;
        } finally {
            setDataLoading(false);
        }
    };

    // Načtení obecných monitoring dat pro přehled
    useEffect(() => {
        const fetchMonitoringData = async () => {
            if (!website || !website.id) return;

            setDataLoading(true);
            try {
                // Pro Overview tab potřebujeme kombinaci různých dat
                const result = {};

                // Základní data o WordPress verzi, SSL, apod.
                try {
                    const wpResponse = await websiteService.getMonitoringResults(website.id, 'wordpress');
                    if (wpResponse && wpResponse.data) {
                        result.wordpress = wpResponse.data;
                    }
                } catch (e) {
                    console.error("WordPress data not available");
                    websiteService.logApiError(e, "monitoring/wordpress");
                }

                // SSL data
                try {
                    const sslResponse = await websiteService.getMonitoringResults(website.id, 'security');
                    if (sslResponse && sslResponse.data) {
                        result.ssl = sslResponse.data;
                    }
                } catch (e) {
                    console.error("SSL data not available");
                    websiteService.logApiError(e, "monitoring/ssl");
                }

                // Performance data
                try {
                    const perfResponse = await websiteService.getMonitoringResults(website.id, 'performance');
                    if (perfResponse && perfResponse.data) {
                        result.performance = perfResponse.data;
                    }
                } catch (e) {
                    console.error("Performance data not available");
                    websiteService.logApiError(e, "monitoring/performance");
                }

                // Issues/Problems
                try {
                    const issuesResponse = await websiteService.getMonitoringResults(website.id, 'issues');
                    if (issuesResponse && issuesResponse.data) {
                        result.issues = issuesResponse.data;
                    } else {
                        result.issues = [];
                    }
                } catch (e) {
                    console.error("Issues data not available");
                    websiteService.logApiError(e, "monitoring/issues");
                    result.issues = [];
                }

                setMonitoringData(result);
            } catch (error) {
                console.error("Failed to fetch monitoring data:", error);
                setDataError("Nepodařilo se načíst monitorovací data.");
            } finally {
                setDataLoading(false);
            }
        };

        fetchMonitoringData();
    }, [website]);

    // Načtení dat podle aktivní záložky
    useEffect(() => {
        if (!website || !website.id) return;

        if (activeTab !== 'overview' && activeTab !== 'backups') {
            fetchSpecificTabData(website.id, activeTab);
        }
    }, [activeTab, website]);

    const getStatusBadgeVariant = () => {
        switch (website.status) {
            case 'online': return 'success';
            case 'issues': return 'warning';
            case 'offline': return 'danger';
            default: return 'secondary';
        }
    };

    return (
        <Container fluid className="fade-in">
            <div className="d-md-flex justify-content-between align-items-start mb-4">
                <div className="mb-3 mb-md-0">
                    <h1 className="mb-2">{website.name || website.url}</h1>
                    <div className="d-flex align-items-center">
                        <Badge
                            bg={getStatusBadgeVariant()}
                            className="me-3 px-3 py-2"
                        >
                            <i className={`fas fa-${website.status === 'online' ? 'check-circle' : website.status === 'issues' ? 'exclamation-triangle' : 'times-circle'} me-2`}></i>
                            {website.status === 'online' ? 'Online' : website.status === 'issues' ? 'Problémy' : 'Offline'}
                        </Badge>
                        <a href={website.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                            <i className="fas fa-external-link-alt me-2"></i> Otevřít web
                        </a>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={navigateBack}>
                        <i className="fas fa-arrow-left me-2"></i> Zpět
                    </Button>
                    <Button variant="primary">
                        <i className="fas fa-sync-alt me-2"></i> Obnovit data
                    </Button>
                </div>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" className="mb-4">
                    <Nav.Item>
                        <Nav.Link eventKey="overview">
                            <i className="fas fa-home me-2"></i> Přehled
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="performance">
                            <i className="fas fa-tachometer-alt me-2"></i> Výkon
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="seo">
                            <i className="fas fa-search me-2"></i> SEO
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="security">
                            <i className="fas fa-shield-alt me-2"></i> Bezpečnost
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="backups">
                            <i className="fas fa-database me-2"></i> Zálohy
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="api">
                            <i className="fas fa-key me-2"></i> API
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="overview">
                        {dataLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" role="status" variant="primary" />
                            </div>
                        ) : dataError ? (
                            <Alert variant="danger">{dataError}</Alert>
                        ) : (
                            <WebsiteOverview website={website} results={monitoringData} />
                        )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="performance">
                        <WebsitePerformance
                            website={website}
                            results={specificData.performance}
                        />
                    </Tab.Pane>
                    <Tab.Pane eventKey="seo">
                        <WebsiteSEO
                            website={website}
                            results={specificData.seo}
                        />
                    </Tab.Pane>
                    <Tab.Pane eventKey="security">
                        <WebsiteSecurity
                            website={website}
                            results={specificData.security}
                        />
                    </Tab.Pane>
                    <Tab.Pane eventKey="backups">
                        <WebsiteBackups website={website} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="api">
                        <ApiKeyManager website={website} />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </Container>
    );
};

// Hlavní komponenta - obaluje obsah s WebsiteContext
const WebsiteDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { websites, fetchWebsites, loading, error } = useWebsiteStore();
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        if (websites.length === 0) {
            fetchWebsites();
        } else {
            const website = websites.find(w => w.id === parseInt(id));
            if (!website) {
                // Web nebyl nalezen - přesměrovat
                navigate('/websites');
            }
        }
    }, [id, websites, fetchWebsites, navigate]);

    // Najít vybraný web
    const website = websites.find(w => w.id === parseInt(id));

    if (loading && !website) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="m-4">
                <Alert.Heading>Došlo k chybě</Alert.Heading>
                <p>{error}</p>
                <div className="d-flex justify-content-end">
                    <Button variant="outline-danger" onClick={() => navigate('/websites')}>
                        Zpět na seznam webů
                    </Button>
                </div>
            </Alert>
        );
    }

    if (!website) {
        return (
            <Alert variant="warning" className="m-4">
                <Alert.Heading>Web nebyl nalezen</Alert.Heading>
                <p>Požadovaný web nebyl nalezen. Možná byl odstraněn nebo nemáte potřebná oprávnění.</p>
                <div className="d-flex justify-content-end">
                    <Button variant="outline-warning" onClick={() => navigate('/websites')}>
                        Zpět na seznam webů
                    </Button>
                </div>
            </Alert>
        );
    }

    const contextValue = {
        websiteId: id,
        website,
        activeTab,
        setActiveTab,
        isLoading: loading,
        error,
        navigateBack: () => navigate('/websites')
    };

    return (
        <ErrorBoundary>
            <WebsiteProvider value={contextValue}>
                <WebsiteDetailContent />
            </WebsiteProvider>
        </ErrorBoundary>
    );
};

export default WebsiteDetail;